import { useState } from 'react'
import { X, Globe, Plus, Trash2, CheckCircle2, Loader2, AlertTriangle } from 'lucide-react'
import { useAmpDomain } from '../hooks/useAmpDomain'

interface AmpDomainModalProps {
  isOpen: boolean
  onClose: () => void
}

export function AmpDomainModal({ isOpen, onClose }: AmpDomainModalProps) {
  const { state, loading, busyHostname, adding, activate, add, remove, isEnvBaseline } = useAmpDomain(isOpen)
  const [newSubdomain, setNewSubdomain] = useState('')

  if (!isOpen) return null

  const handleActivate = activate
  const handleRemove = remove
  const handleAdd = async () => {
    const ok = await add(newSubdomain.trim())
    if (ok) setNewSubdomain('')
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl bg-dark-800 border border-dark-600 rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between border-b border-dark-600 p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-purple-600">
              <Globe className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">AMP Subdomain Rotation</h2>
              <p className="text-xs text-dark-400">Choose which AMP subdomain serves content. Only one is active at a time.</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-dark-400 hover:bg-dark-700 hover:text-white transition"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {loading && !state ? (
            <div className="flex items-center justify-center py-10 text-dark-400">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : !state ? (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>Could not load AMP domain state. Check network + auth.</span>
            </div>
          ) : (
            <>
              <div className="rounded-lg border border-dark-600 bg-dark-900/50 p-4">
                <p className="text-xs text-dark-400 mb-1">Currently active</p>
                <p className="font-mono text-lg text-white">
                  {state.active ?? <span className="text-dark-500">(none — AMP disabled)</span>}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-white mb-2">Pool</h3>
                <div className="space-y-2">
                  {state.available.length === 0 && (
                    <p className="text-sm text-dark-400 py-4 text-center">Pool is empty. Add a subdomain below.</p>
                  )}
                  {state.available.map(hostname => {
                    const isActive = state.active === hostname
                    const isBaseline = isEnvBaseline(hostname)
                    const isBusy = busyHostname === hostname
                    return (
                      <div
                        key={hostname}
                        className={`flex items-center justify-between gap-3 rounded-lg border p-3 transition ${
                          isActive
                            ? 'border-green-500/40 bg-green-500/10'
                            : 'border-dark-600 bg-dark-900/40'
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-white truncate">{hostname}</span>
                            {isActive && (
                              <span className="flex items-center gap-1 rounded-full bg-green-500/20 px-2 py-0.5 text-xs text-green-300">
                                <CheckCircle2 className="h-3 w-3" /> Active
                              </span>
                            )}
                            {isBaseline && (
                              <span className="rounded-full bg-blue-500/20 px-2 py-0.5 text-xs text-blue-300">env</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {!isActive && (
                            <button
                              onClick={() => handleActivate(hostname)}
                              disabled={isBusy}
                              className="rounded-lg bg-primary-500 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-primary-600 disabled:opacity-50"
                            >
                              {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Activate'}
                            </button>
                          )}
                          {!isBaseline && (
                            <button
                              onClick={() => handleRemove(hostname)}
                              disabled={isBusy}
                              className="rounded-lg border border-red-500/40 bg-red-500/10 p-1.5 text-red-400 transition hover:bg-red-500/20 disabled:opacity-50"
                              title="Remove from pool"
                              aria-label={`Remove ${hostname}`}
                            >
                              {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-white mb-2">Add subdomain</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSubdomain}
                    onChange={e => setNewSubdomain(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAdd()}
                    placeholder="e.g. amp4  or  amp4.vndta.cc"
                    className="flex-1 rounded-lg border border-dark-600 bg-dark-900/50 px-3 py-2 text-sm text-white placeholder-dark-500 focus:border-primary-500 focus:outline-none"
                  />
                  <button
                    onClick={handleAdd}
                    disabled={adding || !newSubdomain.trim()}
                    className="flex items-center gap-1.5 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-primary-600 disabled:opacity-50"
                  >
                    {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                    Add
                  </button>
                </div>
                <p className="mt-2 text-xs text-dark-400">
                  Bare subdomains are resolved against HOME_DOMAIN. Full hostnames accepted too. Only alphanumerics, dots and hyphens.
                </p>
              </div>

              <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-3 text-xs text-yellow-200 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  Adding a subdomain here only updates the runtime pool. You still need to add the DNS record and Worker route in Cloudflare before it will serve traffic. See <span className="font-mono">DEPLOYMENT.md</span>.
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
